import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { paylinksApi } from '../../api/endpoints/paylinks';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Alert';

const paylinkSchema = z.object({
  title: z.string().min(1, 'Required'),
  description: z.string().optional(),
  amount: z.coerce.number().min(0).optional(),
  currency: z.string().optional(),
});

type PaylinkFormValues = z.infer<typeof paylinkSchema>;

export function PaylinkForm() {
  const { storeId, paylinkId } = useParams<{ storeId: string; paylinkId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!paylinkId && paylinkId !== 'create';

  const { data: paylinkData } = useQuery({
    queryKey: ['paylink', storeId, paylinkId],
    queryFn: () => paylinksApi.get(storeId!, paylinkId!),
    enabled: !!storeId && isEdit,
  });

  const { register, handleSubmit, formState: { errors }, reset } = useForm<PaylinkFormValues>({
    resolver: zodResolver(paylinkSchema),
  });

  useEffect(() => {
    if (paylinkData?.paylink) {
      const pl = paylinkData.paylink;
      reset({
        title: pl.title ?? '',
        description: pl.description ?? '',
        amount: pl.amount,
        currency: pl.currency ?? '',
      });
    }
  }, [paylinkData, reset]);

  const createMutation = useMutation({
    mutationFn: (data: PaylinkFormValues) =>
      paylinksApi.create(storeId!, { ...data, products: {} }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['paylinks', storeId] });
      toast('success', t('paylinks.createSuccess'));
      if (result.link || result.paylinkId) {
        const paylinkUrl = `https://${storeId}.00stores.com/paylink/${result.link || result.paylinkId}`;
        navigator.clipboard.writeText(paylinkUrl);
        toast('info', t('paylinks.linkCopied'));
      }
      navigate(`/${storeId}/paylinks`);
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateMutation = useMutation({
    mutationFn: (data: PaylinkFormValues) =>
      paylinksApi.update(storeId!, paylinkId!, { ...data, products: {} }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paylinks', storeId] });
      queryClient.invalidateQueries({ queryKey: ['paylink', storeId, paylinkId] });
      toast('success', t('paylinks.updateSuccess'));
      navigate(`/${storeId}/paylinks`);
    },
    onError: () => toast('error', t('common.error')),
  });

  const onSubmit = (data: PaylinkFormValues) => {
    if (isEdit) updateMutation.mutate(data);
    else createMutation.mutate(data);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/${storeId}/paylinks`)}
          className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold">
          {isEdit ? t('paylinks.editPaylink') : t('paylinks.addPaylink')}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <Input
            id="title"
            label={t('common.name')}
            required
            error={errors.title?.message}
            {...register('title')}
          />
          <Textarea
            id="description"
            label={t('common.description')}
            rows={3}
            {...register('description')}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              id="amount"
              label={t('common.price')}
              type="number"
              step="0.01"
              {...register('amount')}
            />
            <Input
              id="currency"
              label={t('paylinks.currency')}
              placeholder="SAR"
              {...register('currency')}
            />
          </div>
        </section>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => navigate(`/${storeId}/paylinks`)}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
